import { PublicClientApplication, LogLevel, InteractionRequiredAuthError } from '@azure/msal-browser';
import { setAuthDataToLocalStorage, setAuthDataToStore, clearAuthDataFromLocalStorage, getAuthEmailFromLocalStorage, buildAuthority } from '@/util/auth/helpers';
import { AuthServiceEnum } from '@/util/auth/enums';
import config from '@/config';
import { Pages } from '@/util/settings/enums';
import useParseQueryString from '@/hooks/useParseQueryString';
const MSAL_CONFIG = {
    auth: {
        clientId: config.azureAd.clientId,
        authority: buildAuthority(config.azureAd.policyNameSignUp),
        knownAuthorities: [`${config.azureAd.instance}.b2clogin.com`]
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        break;
                    case LogLevel.Info:
                        console.info(message);
                        break;
                    case LogLevel.Verbose:
                        console.debug(message);
                        break;
                    case LogLevel.Warning:
                        console.warn(message);
                        break;
                    default:
                        console.info(message);
                        break;
                }
            }
        }
    }
};
class AuthService {
    constructor() {
        this.isIE = window.navigator.userAgent.indexOf('MSIE ') > 0 ||
            window.navigator.userAgent.indexOf('Trident/') > 0;
        this.store = null;
        this.router = null;
        this.MSALObj = new PublicClientApplication(MSAL_CONFIG);
        this.account = null;
        this.scopes = ['openid', 'profile'];
        this.loginPopupRequest = {
            scopes: this.scopes
        };
        this.endJourneyLocationKey = 'endJourneyLocation';
        this.loginRedirectRequest = {
            ...this.loginPopupRequest,
            redirectUri: config.url.redirect.baseRedirectUrl
        };
        this.silentLoginRequest = {
            loginHint: null,
            redirectUri: config.url.redirect.baseRedirectUrl
        };
    }
    init(store, router) {
        this.store = store;
        this.router = router;
    }
    getMSALObj() {
        return this.MSALObj;
    }
    setAccount(account) {
        this.account = account;
    }
    setAccountAndData(response) {
        this.account = response.account;
        setAuthDataToLocalStorage(response);
        setAuthDataToStore(this.store, response);
    }
    getAccount() {
        const currentAccounts = this.MSALObj.getAllAccounts();
        if (currentAccounts.length > 1) {
            // Add choose account code here
            console.log('Multiple accounts detected, need to add choose account code.', currentAccounts);
            return currentAccounts[0];
        }
        if (currentAccounts.length === 1) {
            return currentAccounts[0];
        }
        console.log('No accounts detected');
        return null;
    }
    async loadAuthModule() {
        try {
            const response = await this.MSALObj.handleRedirectPromise();
            const isIframe = window.parent !== window;
            console.log('loadAuthModule Response: ', response);
            if (!isIframe)
                this.handleRedirectPromiseResponse(response);
        }
        catch (error) {
            console.error(error);
            this.router?.push(Pages.SIGNIN);
            window.location.reload();
        }
    }
    handleRedirectPromiseResponse(response) {
        const isSignInPage = this.router?.currentRoute.name === Pages.SIGNIN;
        this.saveOriginallyAccessedUrl(isSignInPage);
        if (response !== null) {
            this.setAccountAndData(response);
            if (isSignInPage)
                this.handleSignInRedirect();
        }
        else
            isSignInPage ? this.handleSignInPage() : this.handleAnyOtherPage();
    }
    saveOriginallyAccessedUrl(isSignInPage) {
        const endJourneyLocation = localStorage.getItem(this.endJourneyLocationKey);
        if (!endJourneyLocation && !isSignInPage) {
            localStorage.setItem(this.endJourneyLocationKey, window.location.href);
        }
    }
    handleSignInPage() {
        this.account = this.getAccount();
        if (this.account)
            this.handleSignInPageSsoSilent();
        else
            this.triggerSignIn();
    }
    triggerSignIn() {
        const queryStrings = useParseQueryString(window.location.href);
        queryStrings.existing ? this.ExistingSignIn() : this.SignIn();
    }
    handleAnyOtherPage() {
        this.account = this.getAccount();
        this.account ? this.attemptSsoSilent() : this.login(AuthServiceEnum.LOGIN_REDIRECT);
    }
    async handleSignInPageSsoSilent() {
        try {
            await this.handleSsoSilent();
            this.handleSignInRedirect();
        }
        catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                this.triggerSignIn();
            }
        }
    }
    handleSignInRedirect() {
        const redirect = localStorage.getItem(this.endJourneyLocationKey);
        if (redirect) {
            const path = redirect.split(config.url.redirect.baseRedirectUrl)[1];
            if (path) {
                console.log('endJourneyLocationKey path is ', path);
                this.router?.push(path);
            }
            else {
                console.log('endJourneyLocationKey path is undefined');
                this.router?.push(`/${Pages.SIGNUP}`);
            }
            localStorage.removeItem(this.endJourneyLocationKey);
        }
        else {
            this.router?.push(`/${Pages.SIGNUP}`);
        }
    }
    async handleSsoSilent() {
        this.silentLoginRequest.loginHint = getAuthEmailFromLocalStorage();
        const response = await this.MSALObj.ssoSilent(this.silentLoginRequest);
        this.setAccountAndData(response);
    }
    async attemptSsoSilent() {
        try {
            this.silentLoginRequest.loginHint = getAuthEmailFromLocalStorage();
            console.log('SILENTLOGIN REQUEST: ', this.silentLoginRequest);
            const response = await this.MSALObj.ssoSilent(this.silentLoginRequest);
            console.log('ATTEMPT SSOSILENT RESPONSE SUCCESS: ', response);
            this.setAccount(this.getAccount());
            setAuthDataToLocalStorage(response);
            setAuthDataToStore(this.store, response);
        }
        catch (error) {
            console.log('ATTEMPT SSOSILENT RESPONSE ERROR: ', error);
            if (error instanceof InteractionRequiredAuthError) {
                this.login(AuthServiceEnum.LOGIN_REDIRECT);
            }
        }
    }
    async login(loginType) {
        const type = this.isIE ? AuthServiceEnum.LOGIN_REDIRECT : loginType;
        try {
            if (type === AuthServiceEnum.LOGIN_POPUP) {
                const response = await this.MSALObj.loginPopup(this.loginPopupRequest);
                this.handleRedirectPromiseResponse(response);
            }
            else if (type === AuthServiceEnum.LOGIN_REDIRECT) {
                this.MSALObj.loginRedirect(this.loginRedirectRequest);
            }
        }
        catch (error) {
            console.error(`LOGIN ${type} ERROR: ${error}`);
        }
    }
    async SignIn() {
        try {
            this.MSALObj.loginRedirect({
                authority: buildAuthority(config.azureAd.policyNameSignInOrMigrate),
                scopes: ['openid'],
                prompt: 'select_account'
            });
        }
        catch (error) {
            console.error(`Sign in and migrate ERROR: ${error}`);
        }
    }
    async ExistingSignIn() {
        try {
            this.MSALObj.loginRedirect({
                authority: buildAuthority(config.azureAd.policyNameSignInExisting),
                scopes: ['openid'],
                prompt: 'select_account'
            });
        }
        catch (error) {
            console.error(`Existing sign in and migrate ERROR: ${error}`);
        }
    }
    logout() {
        const logOutRequest = {
            account: this.account
        };
        console.log('logOutRequest', logOutRequest);
        this.MSALObj.logout(logOutRequest);
        clearAuthDataFromLocalStorage();
    }
    async getTokenSilent(getTokenSilentType) {
        const acquireTokenType = this.isIE
            ? AuthServiceEnum.ACQUIRE_TOKEN_REDIRECT
            : getTokenSilentType;
        const getAuthority = () => {
            const accountIdLowercased = this.account.homeAccountId.toLowerCase();
            if (accountIdLowercased.includes(config.azureAd.policyNameSignInOrMigrate.toLowerCase())) {
                return { authority: buildAuthority(config.azureAd.policyNameSignInOrMigrate) };
            }
            if (accountIdLowercased.includes(config.azureAd.policyNameSignInExisting.toLowerCase())) {
                return { authority: buildAuthority(config.azureAd.policyNameSignInExisting) };
            }
            return { authority: buildAuthority(config.azureAd.policyNameSignUp) };
        };
        try {
            const response = await this.MSALObj.acquireTokenSilent({
                scopes: this.scopes,
                account: this.account,
                forceRefresh: true,
                ...getAuthority()
            });
            this.setAccount(response.account);
            setAuthDataToLocalStorage(response);
            setAuthDataToStore(this.store, response);
            console.log('GETTOKEN SILENT TOKEN ACQUISITION SUCCESS: ', response);
            return response.idToken;
        }
        catch (acquireTokenSilentError) {
            console.log('GETTOKEN SILENT TOKEN ACQUISITION ERROR: ', acquireTokenSilentError);
            if (acquireTokenSilentError instanceof InteractionRequiredAuthError) {
                console.log(`ACQUIRING TOKEN USING ${acquireTokenType}`);
                try {
                    const response = await this.MSALObj[acquireTokenType]({ scopes: this.scopes });
                    this.setAccount(response.account);
                    setAuthDataToLocalStorage(response);
                    setAuthDataToStore(this.store, response);
                    console.log(`ACQUIRING TOKEN USING ${acquireTokenType} SUCCESS: `, response);
                    return response.idToken;
                }
                catch (error) {
                    console.log(`ACQUIRING TOKEN USING ${acquireTokenType} ERROR: `, error);
                    throw error;
                }
            }
            else {
                console.log('SILENT TOKEN ACQUISITION ERROR NOT AN INSTANCE OF InteractionRequiredAuthError: ', acquireTokenSilentError);
                throw acquireTokenSilentError;
            }
        }
    }
}
export const authService = new AuthService();
window.addEventListener('load', () => {
    console.log('AUTH MODULE IS LOADED');
    authService.loadAuthModule();
});
export default (store, router) => authService.init(store, router);
